/**
 * VERXA — Premium Streetwear Master Script
 * Modern Vanilla ES6, Zero bloated dependencies
 */

(function() {
  'use strict';

  // Demo Cart Store (Used seamlessly if not on live Shopify domain)
  const isShopify = typeof window.Shopify !== 'undefined' && window.Shopify.shop;
  let demoCart = {
    items: [
      {
        key: 'demo-item-1',
        id: 'var-l',
        title: 'BORN TO BE DIFFERENT — OVERSIZED TEE',
        variant_title: 'L / VINTAGE WASHED BLACK',
        price: 5800,
        final_line_price: 5800,
        quantity: 1,
        image: 'product-born-front.svg'
      }
    ],
    item_count: 1,
    total_price: 5800
  };

  /* ==========================================================================
     Format Money Helper
     ========================================================================== */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /* ==========================================================================
     1. DRAWER CONTROLLER (Cart & Mobile Navigation)
     ========================================================================== */
  class DrawerManager {
    constructor() {
      this.initTriggers();
    }

    initTriggers() {
      // Open triggers
      document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-drawer-open]');
        if (openBtn) {
          e.preventDefault();
          const targetDrawerId = openBtn.dataset.drawerOpen;
          this.open(targetDrawerId);
        }

        const closeBtn = e.target.closest('[data-drawer-close]');
        if (closeBtn) {
          e.preventDefault();
          const targetDrawerId = closeBtn.dataset.drawerClose;
          this.close(targetDrawerId);
        }
      });

      // Escape key to close drawers & modals
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAll();
        }
      });
    }

    open(id) {
      const drawer = document.getElementById(id) || document.getElementById(`${id}-drawer`);
      if (drawer) {
        drawer.classList.add('active');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('drawer-open');
      }
    }

    close(id) {
      const drawer = document.getElementById(id) || document.getElementById(`${id}-drawer`);
      if (drawer) {
        drawer.classList.remove('active');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('drawer-open');
      }
    }

    closeAll() {
      document.querySelectorAll('.cart-drawer, .mobile-drawer, .search-modal, .modal').forEach(el => {
        el.classList.remove('active');
        el.setAttribute('aria-hidden', 'true');
      });
      document.body.classList.remove('drawer-open', 'modal-open');
    }
  }

  /* ==========================================================================
     2. MODAL CONTROLLER (Search & Size Guide)
     ========================================================================== */
  class ModalManager {
    constructor() {
      this.initTriggers();
      this.initSizeGuideUnits();
    }

    initTriggers() {
      document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-modal-open]');
        if (openBtn) {
          e.preventDefault();
          const modalId = openBtn.dataset.modalOpen;
          this.open(modalId);
        }

        const closeBtn = e.target.closest('[data-modal-close]');
        if (closeBtn) {
          e.preventDefault();
          const modalId = closeBtn.dataset.modalClose || closeBtn.closest('.modal, .search-modal').id;
          this.close(modalId);
        }
      });
    }

    open(id) {
      const modal = document.getElementById(id);
      if (modal) {
        modal.classList.add('active');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');

        const input = modal.querySelector('input[type="search"], input[autofocus]');
        if (input) setTimeout(() => input.focus(), 150);
      }
    }

    close(id) {
      const modal = document.getElementById(id);
      if (modal) {
        modal.classList.remove('active');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('modal-open');
      }
    }

    initSizeGuideUnits() {
      const toggleWrap = document.querySelector('.size-guide__unit-toggle');
      if (!toggleWrap) return;

      const buttons = toggleWrap.querySelectorAll('.unit-btn');
      const tableRows = document.querySelectorAll('#size-table tbody tr');

      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          buttons.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          const unit = btn.dataset.unit; // 'in' or 'cm'

          tableRows.forEach(row => {
            const rawData = row.dataset[unit];
            if (rawData) {
              const [size, chest, length, shoulder, sleeve] = rawData.split(',');
              const unitSuffix = unit === 'in' ? '"' : ' cm';
              row.querySelector('.cell-chest').textContent = `${chest}${unitSuffix}`;
              row.querySelector('.cell-length').textContent = `${length}${unitSuffix}`;
              row.querySelector('.cell-shoulder').textContent = `${shoulder}${unitSuffix}`;
              row.querySelector('.cell-sleeve').textContent = `${sleeve}${unitSuffix}`;
            }
          });
        });
      });
    }
  }

  /* ==========================================================================
     3. SHOPIFY AJAX CART ENGINE
     ========================================================================== */
  class CartEngine {
    constructor() {
      this.drawerEl = document.getElementById('cart-drawer');
      this.itemsContainer = document.getElementById('cart-drawer-items');
      this.badgeEl = document.getElementById('cart-icon-bubble');
      this.subtotalEl = document.getElementById('cart-drawer-subtotal');
      this.countEl = document.getElementById('cart-drawer-count');
      this.footerEl = document.getElementById('cart-drawer-footer');
      this.meterFill = document.getElementById('shipping-meter-fill');
      this.meterText = document.getElementById('shipping-meter-text');

      this.initCartDrawerEvents();
      this.initProductForm();
      this.updateUI(demoCart);
    }

    initProductForm() {
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('.shopify-product-form');
        if (!form) return;

        e.preventDefault();
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.classList.add('btn--loading');

        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1', 10);

        if (isShopify) {
          fetch(window.VERXA.routes.cart_add_url, {
            method: 'POST',
            body: formData,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
          .then(res => res.json())
          .then(item => {
            return fetch(window.VERXA.routes.cart_url + '.js').then(r => r.json());
          })
          .then(cart => {
            this.updateUI(cart);
            if (window.drawerManager) window.drawerManager.open('cart-drawer');
          })
          .catch(err => console.error('Shopify Cart Error:', err))
          .finally(() => {
            if (submitBtn) submitBtn.classList.remove('btn--loading');
          });
        } else {
          // Prototype / Live Demo Handling
          setTimeout(() => {
            const sizeLabel = document.querySelector('.size-pill.active')?.dataset.size || 'L';
            const colorLabel = document.getElementById('selected-color-label')?.textContent || 'VINTAGE WASHED BLACK';
            const price = 5800;

            const existingItem = demoCart.items.find(i => i.id === variantId);
            if (existingItem) {
              existingItem.quantity += quantity;
              existingItem.final_line_price = existingItem.quantity * price;
            } else {
              demoCart.items.push({
                key: `item-${Date.now()}`,
                id: variantId,
                title: 'BORN TO BE DIFFERENT — OVERSIZED TEE',
                variant_title: `${sizeLabel} / ${colorLabel}`,
                price: price,
                final_line_price: price * quantity,
                quantity: quantity,
                image: 'product-born-front.svg'
              });
            }

            this.calculateDemoTotals();
            this.updateUI(demoCart);
            if (window.drawerManager) window.drawerManager.open('cart-drawer');
            if (submitBtn) submitBtn.classList.remove('btn--loading');
          }, 400);
        }
      });
    }

    initCartDrawerEvents() {
      if (!this.drawerEl) return;

      this.drawerEl.addEventListener('click', (e) => {
        // Quantity change
        const qtyBtn = e.target.closest('[data-change-qty]');
        if (qtyBtn) {
          const action = qtyBtn.dataset.changeQty;
          const key = qtyBtn.dataset.key;
          this.changeQuantity(key, action);
        }

        // Remove item
        const removeBtn = e.target.closest('[data-remove-key]');
        if (removeBtn) {
          const key = removeBtn.dataset.removeKey;
          this.removeItem(key);
        }
      });
    }

    calculateDemoTotals() {
      demoCart.item_count = demoCart.items.reduce((sum, item) => sum + item.quantity, 0);
      demoCart.total_price = demoCart.items.reduce((sum, item) => sum + item.final_line_price, 0);
    }

    changeQuantity(key, action) {
      if (isShopify) {
        const item = this.findItemByKey(key);
        if (!item) return;
        const newQty = action === 'plus' ? item.quantity + 1 : Math.max(0, item.quantity - 1);
        fetch(window.VERXA.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: key, quantity: newQty })
        })
        .then(res => res.json())
        .then(cart => this.updateUI(cart));
      } else {
        const item = demoCart.items.find(i => i.key === key);
        if (item) {
          if (action === 'plus') {
            item.quantity += 1;
          } else {
            item.quantity -= 1;
          }
          if (item.quantity <= 0) {
            demoCart.items = demoCart.items.filter(i => i.key !== key);
          } else {
            item.final_line_price = item.quantity * item.price;
          }
          this.calculateDemoTotals();
          this.updateUI(demoCart);
        }
      }
    }

    removeItem(key) {
      if (isShopify) {
        fetch(window.VERXA.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: key, quantity: 0 })
        })
        .then(res => res.json())
        .then(cart => this.updateUI(cart));
      } else {
        demoCart.items = demoCart.items.filter(i => i.key !== key);
        this.calculateDemoTotals();
        this.updateUI(demoCart);
      }
    }

    updateUI(cart) {
      // Update badge
      if (this.badgeEl) {
        this.badgeEl.textContent = cart.item_count;
        this.badgeEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }
      if (this.countEl) this.countEl.textContent = cart.item_count;

      // Update Subtotal
      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.total_price);

      // Shipping Meter (Threshold: $100)
      const threshold = 10000;
      const percent = Math.min(100, Math.round((cart.total_price / threshold) * 100));
      if (this.meterFill) this.meterFill.style.width = `${percent}%`;
      if (this.meterText) {
        if (cart.total_price >= threshold) {
          this.meterText.innerHTML = '<span>🎉 QUALIFIED FOR FREE EXPRESS SHIPPING</span>';
        } else {
          const remaining = threshold - cart.total_price;
          this.meterText.innerHTML = `<span>ADD <strong>${formatMoney(remaining)}</strong> FOR FREE EXPRESS SHIPPING</span>`;
        }
      }

      // Render Items
      if (!this.itemsContainer) return;

      if (cart.items.length === 0) {
        this.itemsContainer.innerHTML = `
          <div class="cart-drawer__empty" id="cart-empty-view">
            <p class="empty-title">YOUR BAG IS EMPTY</p>
            <p class="empty-desc">Pieces from DROP 01 are strictly limited.</p>
            <a href="/collections/all" class="btn btn--secondary btn--sm" data-drawer-close="cart-drawer">
              EXPLORE THE FIRST DROP
            </a>
          </div>
        `;
        if (this.footerEl) this.footerEl.classList.add('hidden');
      } else {
        if (this.footerEl) this.footerEl.classList.remove('hidden');
        this.itemsContainer.innerHTML = cart.items.map(item => `
          <div class="cart-item" data-line-key="${item.key}">
            <div class="cart-item__media">
              <img src="${item.image || 'product-born-front.svg'}" alt="${item.title}" class="cart-item__img" width="80" height="100">
            </div>
            <div class="cart-item__details">
              <div class="cart-item__header">
                <h3 class="cart-item__title">${item.title}</h3>
                <button type="button" class="cart-item__remove" data-remove-key="${item.key}" aria-label="Remove item">✕</button>
              </div>
              <div class="cart-item__variant-title">${item.variant_title || 'L / WASHED BLACK'}</div>
              <div class="cart-item__footer">
                <div class="cart-item__quantity-stepper">
                  <button type="button" class="drawer-qty-btn" data-change-qty="minus" data-key="${item.key}">−</button>
                  <span class="drawer-qty-val">${item.quantity}</span>
                  <button type="button" class="drawer-qty-btn" data-change-qty="plus" data-key="${item.key}">+</button>
                </div>
                <div class="cart-item__price">${formatMoney(item.final_line_price || item.price * item.quantity)}</div>
              </div>
            </div>
          </div>
        `).join('');
      }
    }
  }

  /* ==========================================================================
     4. PRODUCT GALLERY CONTROLLER
     ========================================================================== */
  class GalleryManager {
    constructor() {
      this.stageWrap = document.getElementById('gallery-main-wrap');
      this.thumbsContainer = document.getElementById('gallery-thumbs');
      this.slides = document.querySelectorAll('.gallery-slide');
      this.thumbs = document.querySelectorAll('.thumb-btn');
      this.currentIdxEl = document.querySelector('.gallery-current-idx');
      this.init();
    }

    init() {
      if (!this.slides.length) return;

      this.thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const index = parseInt(thumb.dataset.index, 10);
          this.goToSlide(index);
        });
      });

      // Mobile Touch Swipe Handling
      let touchStartX = 0;
      let touchEndX = 0;
      if (this.stageWrap) {
        this.stageWrap.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        this.stageWrap.addEventListener('touchend', (e) => {
          touchEndX = e.changedTouches[0].screenX;
          this.handleSwipe(touchStartX, touchEndX);
        }, { passive: true });
      }
    }

    goToSlide(index) {
      this.slides.forEach((s, idx) => s.classList.toggle('active', idx === index));
      this.thumbs.forEach((t, idx) => t.classList.toggle('active', idx === index));
      if (this.currentIdxEl) {
        const padded = (index + 1).toString().padStart(2, '0');
        this.currentIdxEl.textContent = padded;
      }
    }

    handleSwipe(start, end) {
      const diff = start - end;
      if (Math.abs(diff) < 40) return;
      const current = Array.from(this.slides).findIndex(s => s.classList.contains('active'));
      if (diff > 0 && current < this.slides.length - 1) {
        this.goToSlide(current + 1);
      } else if (diff < 0 && current > 0) {
        this.goToSlide(current - 1);
      }
    }
  }

  /* ==========================================================================
     5. VARIANT SELECTOR CONTROLLER
     ========================================================================== */
  class VariantManager {
    constructor() {
      this.sizePills = document.querySelectorAll('.size-pill');
      this.colorSwatches = document.querySelectorAll('.swatch-btn');
      this.hiddenVariantInput = document.getElementById('selected-variant-id');
      this.selectedColorLabel = document.getElementById('selected-color-label');
      this.addToCartBtn = document.getElementById('AddToCart');
      this.init();
    }

    init() {
      // Size Pill Switcher
      this.sizePills.forEach(pill => {
        pill.addEventListener('click', () => {
          if (pill.classList.contains('disabled')) return;
          this.sizePills.forEach(p => p.classList.remove('active'));
          pill.classList.add('active');

          const variantId = pill.dataset.variantId || `var-${pill.dataset.size.toLowerCase()}`;
          if (this.hiddenVariantInput) this.hiddenVariantInput.value = variantId;

          // Update stock status indicator
          const stockIndicator = document.getElementById('stock-status-indicator');
          if (stockIndicator) {
            stockIndicator.innerHTML = '<span class="stock-dot"></span><span class="stock-text">IN STOCK — READY FOR DISPATCH</span>';
          }
          if (this.addToCartBtn) {
            this.addToCartBtn.disabled = false;
            this.addToCartBtn.querySelector('.btn-text').textContent = 'ADD TO CART';
          }
        });
      });

      // Color Swatch Switcher
      this.colorSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          this.colorSwatches.forEach(s => s.classList.remove('active'));
          swatch.classList.add('active');
          const colorName = swatch.dataset.color;
          if (this.selectedColorLabel) this.selectedColorLabel.textContent = colorName;
        });
      });

      // Quantity stepper buttons on product page
      const qtyMinus = document.querySelector('[data-qty-action="minus"]');
      const qtyPlus = document.querySelector('[data-qty-action="plus"]');
      const qtyInput = document.getElementById('Quantity');

      if (qtyMinus && qtyInput) {
        qtyMinus.addEventListener('click', () => {
          const val = parseInt(qtyInput.value, 10) || 1;
          if (val > 1) qtyInput.value = val - 1;
        });
      }
      if (qtyPlus && qtyInput) {
        qtyPlus.addEventListener('click', () => {
          const val = parseInt(qtyInput.value, 10) || 1;
          if (val < 10) qtyInput.value = val + 1;
        });
      }
    }
  }

  /* ==========================================================================
     6. ACCORDION CONTROLLER
     ========================================================================== */
  class AccordionManager {
    constructor() {
      this.accordions = document.querySelectorAll('.accordion-item');
      this.init();
    }

    init() {
      this.accordions.forEach(item => {
        const header = item.querySelector('.accordion-header');
        const body = item.querySelector('.accordion-body');
        if (!header || !body) return;

        header.addEventListener('click', () => {
          const isActive = item.classList.contains('active');
          item.classList.toggle('active');
          header.setAttribute('aria-expanded', !isActive);
          body.hidden = isActive;
        });
      });
    }
  }

  /* ==========================================================================
     7. STICKY CART BAR (Mobile on scroll past purchase section)
     ========================================================================== */
  class StickyCartManager {
    constructor() {
      this.stickyBar = document.getElementById('sticky-cart-bar');
      this.trigger = document.getElementById('AddToCart');
      this.stickyBtn = document.getElementById('sticky-cart-trigger');
      this.init();
    }

    init() {
      if (!this.stickyBar || !this.trigger) return;

      window.addEventListener('scroll', () => {
        const rect = this.trigger.getBoundingClientRect();
        if (rect.bottom < 0) {
          this.stickyBar.classList.add('visible');
        } else {
          this.stickyBar.classList.remove('visible');
        }
      }, { passive: true });

      if (this.stickyBtn) {
        this.stickyBtn.addEventListener('click', () => {
          this.trigger.click();
        });
      }
    }
  }

  /* ==========================================================================
     INITIALIZATION ON DOM READY
     ========================================================================== */
  document.addEventListener('DOMContentLoaded', () => {
    window.drawerManager = new DrawerManager();
    window.modalManager = new ModalManager();
    window.cartEngine = new CartEngine();
    window.galleryManager = new GalleryManager();
    window.variantManager = new VariantManager();
    window.accordionManager = new AccordionManager();
    window.stickyCartManager = new StickyCartManager();
  });

})();
